/**
 * @description Renders the assign personnel confirmation dialog contents. This element is passed as the message to the `confirm()` api call.
 */

import React from 'react';
import { PropTypes } from 'prop-types';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';

import withErrorBoundary from '@seccon/shared/ui/hocs/with-error-boundary';
import { formatListAsString } from '@seccon/shared/utils/format';

const styles = {
  content: {
    maxHeight: '31.25rem',
    overflowY: 'auto'
  },
  list: {
    listStyleType: 'disc',
    ml: 3,
    mr: 3
  },
  listItem: {
    display: 'list-item'
  },
  text: {
    color: 'text.primary'
  }
}

const AssignConfirmation = ({
  approvedPersonnel,
  contractInfo,
  selectedPersonnel,
  unapprovedPersonnel: unapprovedPersonnelProp
}) => {
  // Combine the selected personnel information with the unapproved personnel list
  const unapprovedPersonnel = React.useMemo(() => (
    unapprovedPersonnelProp.reduce((list, report) => {
      // Find the person info in the selected personnel list based on the personel id included in the unapproved personnel report
      const person = selectedPersonnel.find((entry) => entry.personnelId === report.personnelId);

      if (person) {
        list.push({
          ...person,
          ...report
        })
      }

      return list;
    }, [])
  ), [selectedPersonnel, unapprovedPersonnelProp]);

  /**
   * Renders the unnapproved personnel summary based on the list counts.
   * @returns {string} - unapproved personnel summary
   */
  function getUnapprovedSummary () {
    let summary = 'The following personnel was flagged due to an issue with their clearance settings:';

    // Change the beginnging of the summary, if there is more than one personnel selected
    if (selectedPersonnel.length > 1) {
      summary = `Out of ${ selectedPersonnel.length } selected personnel, `;

      // Change the second half of the sentence based on the number of unapproved personnel
      summary += unapprovedPersonnel.length === 1 ? 'the following was flagged due to an issue with their clearance settings:' : (
        `the following ${ unapprovedPersonnel.length } were flagged due to an issue with their clearance settings:`
      );
    }

    return `Are you sure you want to continue with the assignment?  ${ summary }`;
  }

  /**
   * Prepares the unapproved personnel statement for an individual person
   * @param {object} person - person report
   * @returns {string} - unnapproved personnel statement based on the reprort results
   */
  function getUnapprovedPersonnelStatement (person) {
    const statements = [];
    const accessRequired = person.accessRequired?.label ?? 'None';
    const eligibility = person.eligibility?.label ?? 'None';
    const contractClearanceLevel = contractInfo?.clearanceLevel ?? 'None';
    let particle = 'has';

    // Add a statement if the person has an unsupported access required value
    if (person.unsupportedAccessRequired) {
      statements.push(`an access required level that cannot be validated (${ person.unsupportedAccessRequired })`);
    }

    // Add a statement if the access required is greater than the contract clearance level
    if (person.isAccessRequiredGreaterThanContractClearanceLevel) {
      statements.push(`an access required level (${ accessRequired }) greater than the contract clearance level (${ contractClearanceLevel })`);
    }

    // Add a statement if the person has an unsupported eligibility value
    if (person.unsupportedEligibility) {
      statements.push(`an eligibility level that cannot be validated (${ person.unsupportedEligibility })`);
    }

    // Add a statement if the person has an access required greater than their eligibility
    if (person.isAccessRequiredGreaterThanEligibility) {
      statements.push(`an access required level (${ accessRequired }) greater than their eligibility level (${ eligibility })`);
    }

    // Add a statement if the special accesses indicated is not provided in this contract
    if (person.flaggedSpecialAccesses.length > 0) {
      const selectedSpecialAccesses = []
      // The particle at the beginning of the statement isn't necessary if this is the first statement ("Test User has requires" makes no sense, but "Test User requires" does)
      particle = statements.length === 0 ? '' : particle;
      person.flaggedSpecialAccesses.forEach((specialAccessId) => {
        person.specialAccesses.forEach((item) => {
          if (item.value === specialAccessId) {
            if (!selectedSpecialAccesses.includes(item.label)) {
              selectedSpecialAccesses.push(item.label);
            }
          }
        });
      });

      statements.push(`requires special accesses that are missing from the contract (${ formatListAsString(selectedSpecialAccesses) })`);
    }

    // Combine the list of statements into one sentence
    return `${ person.firstName } ${ person.lastName } ${ particle } ${ formatListAsString(statements) }.`;
  }

  // Prepared the summary of the approved personnel
  // The results of which are memoized so we don't have to iterate throught the list of approved personnel to prepare the summary during every render
  const approvedSummary = React.useMemo(() => {
    const list = [];

    // Get a list of the first and last name of every approved personnel
    for (const personnelId of approvedPersonnel) {
      // Find the person info in the selected personnel list based on the personel id included in the approved personnel list
      const person = selectedPersonnel.find((entry) => entry.personnelId === personnelId);

      if (person) {
        list.push(`${ person.firstName } ${ person.lastName }`);
      }
    }

    if (list.length === 0) {
      return null;
    } else if (list.length === 1) {
      return `However, ${ list[0] } is an approved personnel.`;
    }

    return `However, the following personnel were approved: ${ formatListAsString(list) }.`;
  }, [approvedPersonnel, selectedPersonnel]);

  return (
    <DialogContent sx={styles.content} dividers>
      <DialogContentText sx={styles.text}>{getUnapprovedSummary()}</DialogContentText>
      <List sx={styles.list}>
        {unapprovedPersonnel.map((report) => (
          <ListItem key={report.personnelId} sx={styles.listItem}>
            {getUnapprovedPersonnelStatement(report)}
          </ListItem>
        ))}
      </List>
      {approvedPersonnel.length > 0 && (
        <DialogContentText sx={styles.text}>{approvedSummary}</DialogContentText>
      )}
    </DialogContent>
  );
}

AssignConfirmation.propTypes = {
  approvedPersonnel: PropTypes.arrayOf(PropTypes.number).isRequired,
  contractInfo: PropTypes.object.isRequired,
  selectedPersonnel: PropTypes.arrayOf(PropTypes.object).isRequired,
  unapprovedPersonnel: PropTypes.arrayOf(PropTypes.object).isRequired
}

export default withErrorBoundary(AssignConfirmation);
